import type { UserResponse } from '@/shared/api';
import { fullName, userStatus } from '../userStatus';

export type BulkActionId = 'deactivate' | 'reactivate' | 'resend';

export interface BulkOutcomeEntry {
  id: string;
  label: string;
  reason: string;
}

export interface BulkOutcome {
  succeeded: string[];
  failed: BulkOutcomeEntry[];
  /** Nicht versucht, weil die Aktion für diesen Zustand keinen Sinn ergibt. */
  skipped: BulkOutcomeEntry[];
}

interface BulkActionDefinition {
  id: BulkActionId;
  label: string;
  /** Nur diese Konten werden angefasst; alle übrigen erscheinen als übersprungen. */
  applies: (user: UserResponse, currentUserId?: string) => boolean;
  /** Warum ein Konto übersprungen wurde — steht so im Ergebnis. */
  skipReason: (user: UserResponse, currentUserId?: string) => string;
  destructive?: boolean;
}

/**
 * Die drei Sammelaktionen der Benutzerliste.
 *
 * <p>Es gibt bewusst keinen Sammel-Endpunkt: das Frontend ruft die vorhandenen Einzelendpunkte
 * nacheinander auf und berichtet, was davon durchkam. Ein neuer Bulk-Endpunkt müsste
 * entweder eine Transaktion über fremde Aggregate aufspannen oder Atomizität vortäuschen —
 * beides wäre teurer und unehrlicher als ein Teilergebnis.</p>
 *
 * <p>`applies` ist kein Komfort, sondern Wahrheit: der Server nimmt „Einladung erneut senden"
 * für ein bestätigtes Konto nicht an. Ohne diese Vorauswahl bestünde ein Sammellauf zur Hälfte
 * aus Fehlern, die niemand verursacht hat.</p>
 */
export const BULK_ACTIONS: Record<BulkActionId, BulkActionDefinition> = {
  deactivate: {
    id: 'deactivate',
    label: 'Zugang sperren',
    destructive: true,
    applies: (u, currentUserId) => u.isActive && u.id !== currentUserId,
    skipReason: (u, currentUserId) =>
      u.id === currentUserId ? 'das eigene Konto' : 'Zugang ist bereits gesperrt',
  },
  reactivate: {
    id: 'reactivate',
    label: 'Zugang freigeben',
    applies: (u) => !u.isActive,
    skipReason: () => 'Zugang ist bereits frei',
  },
  resend: {
    id: 'resend',
    label: 'Einladung erneut senden',
    applies: (u) => userStatus(u) === 'Invited',
    skipReason: (u) => (u.isActive ? 'Einladung ist bereits angenommen' : 'Zugang ist gesperrt'),
  },
};

export interface BulkPlan {
  applicable: UserResponse[];
  skipped: BulkOutcomeEntry[];
}

export function planBulkAction(
  action: BulkActionId,
  users: UserResponse[],
  currentUserId?: string,
): BulkPlan {
  const definition = BULK_ACTIONS[action];
  const applicable: UserResponse[] = [];
  const skipped: BulkOutcomeEntry[] = [];
  for (const user of users) {
    if (definition.applies(user, currentUserId)) {
      applicable.push(user);
    } else {
      skipped.push({
        id: user.id,
        label: fullName(user),
        reason: definition.skipReason(user, currentUserId),
      });
    }
  }
  return { applicable, skipped };
}

/**
 * Führt die Einzelaufrufe nacheinander aus und sammelt beide Seiten ein.
 *
 * <p>Nacheinander und nicht parallel: die Aufrufe schreiben, und ein Sammellauf über zwanzig
 * Konten soll das Ratelimit nicht in einer Sekunde leerlaufen. Ein Fehler bricht den Lauf
 * nicht ab — sonst wäre das Ergebnis von der Reihenfolge abhängig.</p>
 */
export async function runBulkAction(
  plan: BulkPlan,
  call: (user: UserResponse) => Promise<unknown>,
  describeError: (error: unknown) => Promise<string>,
): Promise<BulkOutcome> {
  const succeeded: string[] = [];
  const failed: BulkOutcomeEntry[] = [];
  for (const user of plan.applicable) {
    try {
      await call(user);
      succeeded.push(user.id);
    } catch (error) {
      failed.push({ id: user.id, label: fullName(user), reason: await describeError(error) });
    }
  }
  return { succeeded, failed, skipped: plan.skipped };
}

/** „7 von 9 erfolgreich" — die Zahl, die zuerst gelesen wird, ist die ehrliche. */
export function summarizeBulkOutcome(outcome: BulkOutcome): string {
  const attempted = outcome.succeeded.length + outcome.failed.length;
  if (attempted === 0) {
    return 'Nichts ausgeführt.';
  }
  if (outcome.failed.length === 0) {
    return `${outcome.succeeded.length} von ${attempted} erfolgreich.`;
  }
  return `${outcome.succeeded.length} von ${attempted} erfolgreich, ${outcome.failed.length} fehlgeschlagen.`;
}
